import { closeToast, showConfirmDialog, showFailToast, showLoadingToast } from "vant";
import { isInChina } from "@/utils/commonUtils";
import messages from "@/locales/i18n-messages.js";
import { EN_US } from "@/effects/switchLanguageEffect";

/** 两点球面距离（km），用于判断起终点是否重合 */
function haversineKm(lat1, lng1, lat2, lng2) {
  const a1 = Number(lat1);
  const o1 = Number(lng1);
  const a2 = Number(lat2);
  const o2 = Number(lng2);
  if (![a1, o1, a2, o2].every((v) => Number.isFinite(v))) return Infinity;
  const R = 6371;
  const t1 = (a1 * Math.PI) / 180;
  const t2 = (a2 * Math.PI) / 180;
  const dLat = ((a2 - a1) * Math.PI) / 180;
  const dLng = ((o2 - o1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(t1) * Math.cos(t2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

let cachedBrowserPosition = null;
let cachedBrowserPositionAt = 0;
const BROWSER_POSITION_CACHE_MS = 5 * 60 * 1000;
const GEO_CACHE_STORAGE_KEY = "castrol_nav_geo_cache_v1";
/** 本次会话内 geolocation 是否被用户明确拒绝（以 getCurrentPosition 为准） */
let geolocationAccessDenied = false;

const clearBrowserPositionCache = () => {
  cachedBrowserPosition = null;
  cachedBrowserPositionAt = 0;
  try {
    sessionStorage.removeItem(GEO_CACHE_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const markGeolocationGranted = () => {
  geolocationAccessDenied = false;
};

const markGeolocationDenied = () => {
  geolocationAccessDenied = true;
  clearBrowserPositionCache();
};

const saveBrowserPosition = (lat, lng) => {
  cachedBrowserPosition = { lat, lng };
  cachedBrowserPositionAt = Date.now();
  try {
    sessionStorage.setItem(
      GEO_CACHE_STORAGE_KEY,
      JSON.stringify({ lat, lng, at: cachedBrowserPositionAt })
    );
  } catch {
    // ignore
  }
};

const getCachedBrowserPosition = () => {
  if (cachedBrowserPosition) {
    if (Date.now() - cachedBrowserPositionAt > BROWSER_POSITION_CACHE_MS) {
      clearBrowserPositionCache();
    } else {
      return { ...cachedBrowserPosition };
    }
  }

  try {
    const raw = sessionStorage.getItem(GEO_CACHE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lng = Number(parsed?.lng);
    const at = Number(parsed?.at);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(at)) {
      clearBrowserPositionCache();
      return null;
    }
    if (Date.now() - at > BROWSER_POSITION_CACHE_MS) {
      clearBrowserPositionCache();
      return null;
    }
    cachedBrowserPosition = { lat, lng };
    cachedBrowserPositionAt = at;
    return { lat, lng };
  } catch {
    return null;
  }
};

const getPositionAccuracyM = (position) => {
  const accuracy = Number(position?.coords?.accuracy);
  return Number.isFinite(accuracy) ? accuracy : Infinity;
};

const isLikelyMobileDevice = () => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return /android|iphone|ipad|ipod|mobile/i.test(ua);
};

const isLikelyMacDesktop = () => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return /macintosh|mac os x/i.test(ua) && !isLikelyMobileDevice();
};

const queryGeolocationPermissionState = async () => {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return null;
  }
  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state;
  } catch {
    return null;
  }
};

/** 桌面 Chrome 无 GPS；手机则优先 GPS */
const getNavigationGeoProfile = (permissionState = null) => {
  if (isLikelyMobileDevice()) {
    return {
      isDesktop: false,
      fastHighAccuracy: true,
      fastTimeout: 20000,
      maximumAge: 0,
      maxAccuracyM: 150,
      refineTimeout: 12000,
      acceptAnyFix: false,
      overallTimeout: 35000,
    };
  }

  const needsPermissionPrompt =
    permissionState === "prompt" || permissionState === null;
  const alreadyGranted = permissionState === "granted";
  const isMacDesktop = isLikelyMacDesktop();

  return {
    isDesktop: true,
    isMacDesktop,
    fastHighAccuracy: false,
    fastTimeout: needsPermissionPrompt ? 60000 : alreadyGranted ? 20000 : 15000,
    // Mac CoreLocation 常 kCLErrorLocationUnknown；优先用系统缓存坐标
    maximumAge: isMacDesktop || alreadyGranted ? 300000 : 60000,
    maxAccuracyM: 800,
    refineTimeout: isMacDesktop ? 45000 : 30000,
    acceptAnyFix: true,
    overallTimeout: needsPermissionPrompt
      ? isMacDesktop
        ? 110000
        : 95000
      : isMacDesktop
        ? 60000
        : 55000,
  };
};

const isAccurateEnoughForNavigation = (position, profile) =>
  getPositionAccuracyM(position) <= profile.maxAccuracyM;

const shouldAcceptNavigationFix = (position, parsed, profile) => {
  if (!parsed) return false;
  if (isAccurateEnoughForNavigation(position, profile)) return true;
  return profile.acceptAnyFix;
};

const readGeolocationCoords = (position) => {
  const lat = Number(position?.coords?.latitude);
  const lng = Number(position?.coords?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
};

const parseGeolocationPosition = (position) => readGeolocationCoords(position);

/**
 * 交互式获取导航起点：必须在用户点击的同步调用栈里立刻 request 定位，
 * 不能先 await Permissions API，否则 Chrome 首次不会弹权限/拿不到坐标。
 */
const acquireNavigationOriginInteractive = (options, { onSuccess, onFailure }) => {
  if (options?.useCurrentLocation === false) {
    onFailure({ reason: "disabled" });
    return () => {};
  }

  geolocationAccessDenied = false;

  const cached = getCachedBrowserPosition();
  if (options.preferCache !== false && cached) {
    onSuccess(wgs84CachedToGcjOrigin(cached));
    return () => {};
  }

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    onFailure({ reason: "unsupported" });
    return () => {};
  }

  let finished = false;
  let watchId = null;
  let watchTimer = null;
  let retryTimer = null;
  let bestParsed = null;
  let bestAccuracyM = Infinity;

  const cleanup = () => {
    if (watchId != null) navigator.geolocation.clearWatch(watchId);
    if (watchTimer != null) clearTimeout(watchTimer);
    if (retryTimer != null) clearTimeout(retryTimer);
    watchId = null;
    watchTimer = null;
    retryTimer = null;
  };

  const finishSuccess = (current) => {
    cleanup();
    saveBrowserPosition(current.lat, current.lng);
    markGeolocationGranted();
    const origin = wgs84CachedToGcjOrigin(current);
    if (finished) {
      onSuccess(origin);
      return;
    }
    finished = true;
    onSuccess(origin);
  };

  const finishFailure = (reason) => {
    if (finished) return;
    finished = true;
    cleanup();
    onFailure({ reason });
  };

  const handleGeoError = (error, retry) => {
    if (error?.code === 1) {
      markGeolocationDenied();
      finishFailure("denied");
      return;
    }
    if (typeof retry === "function") retry();
    else finishFailure("timeout");
  };

  const profile = getNavigationGeoProfile(null);

  const startDesktopWatch = () => {
    if (finished) return;
    if (watchId != null) navigator.geolocation.clearWatch(watchId);
    if (watchTimer != null) clearTimeout(watchTimer);
    const watchTimeout = options.locationWatchTimeout ?? profile.refineTimeout;
    watchId = navigator.geolocation.watchPosition(
      (position) => {
        const parsed = parseGeolocationPosition(position);
        if (parsed) finishSuccess(parsed);
      },
      (error) => {
        if (error?.code === 1) {
          markGeolocationDenied();
          finishFailure("denied");
        }
      },
      {
        enableHighAccuracy: false,
        maximumAge: profile.maximumAge,
        timeout: watchTimeout,
      }
    );
    watchTimer = window.setTimeout(() => {
      if (!finished) {
        finishFailure(profile.isMacDesktop ? "unavailable" : "timeout");
      }
    }, watchTimeout + 1000);
  };

  const tryMacCachedPosition = () => {
    if (finished || !profile.isMacDesktop) {
      startDesktopWatch();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const parsed = parseGeolocationPosition(position);
        if (parsed) finishSuccess(parsed);
        else startDesktopWatch();
      },
      () => startDesktopWatch(),
      {
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 300000,
      }
    );
  };

  const requestDesktopPosition = () => {
    if (finished) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const parsed = parseGeolocationPosition(position);
        if (parsed) finishSuccess(parsed);
        else finishFailure("invalid");
      },
      (error) => {
        if (error?.code === 1) {
          markGeolocationDenied();
          finishFailure("denied");
          return;
        }
        if (error?.code === 2 && profile.isMacDesktop) {
          tryMacCachedPosition();
          return;
        }
        startDesktopWatch();
      },
      {
        enableHighAccuracy: false,
        timeout: options.locationPromptTimeout ?? profile.fastTimeout,
        maximumAge: profile.maximumAge,
      }
    );
  };

  const startMobileAcquisition = () => {
    const watchTimeout = options.locationWatchTimeout ?? profile.refineTimeout;

    const startWatchFallback = () => {
      if (finished) return;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (watchTimer != null) clearTimeout(watchTimer);
      bestParsed = null;
      bestAccuracyM = Infinity;
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          const parsed = parseGeolocationPosition(position);
          if (!parsed) return;
          const accuracyM = getPositionAccuracyM(position);
          if (accuracyM < bestAccuracyM) {
            bestAccuracyM = accuracyM;
            bestParsed = parsed;
          }
          if (shouldAcceptNavigationFix(position, parsed, profile)) {
            finishSuccess(parsed);
          }
        },
        (error) => handleGeoError(error),
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: watchTimeout,
        }
      );
      watchTimer = window.setTimeout(() => {
        if (bestParsed) finishSuccess(bestParsed);
        else finishFailure("timeout");
      }, watchTimeout + 1000);
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const parsed = parseGeolocationPosition(position);
        if (shouldAcceptNavigationFix(position, parsed, profile)) {
          finishSuccess(parsed);
        } else {
          startWatchFallback();
        }
      },
      (error) => handleGeoError(error, startWatchFallback),
      {
        enableHighAccuracy: profile.fastHighAccuracy,
        timeout: options.locationPromptTimeout ?? profile.fastTimeout,
        maximumAge: profile.maximumAge,
      }
    );
  };

  // 同步发起定位（不要 await queryGeolocationPermissionState）
  if (profile.isDesktop) {
    requestDesktopPosition();
  } else {
    startMobileAcquisition();
  }

  return () => {
    cleanup();
  };
};

const NAV_OVERLAY_Z_INDEX = 6000;

const blockedNavMsg = (lang, key) => {
  const locale = lang === "en" ? EN_US : "zh-CN";
  const home = messages[locale]?.app?.home;
  const fallback = messages["zh-CN"]?.app?.home;
  return home?.[`map_nav_popup_blocked_${key}`] || fallback?.[`map_nav_popup_blocked_${key}`] || key;
};

const blockedNavCancel = (lang) => {
  const locale = lang === "en" ? EN_US : "zh-CN";
  return (
    messages[locale]?.app?.home?.map_nav_cancel ||
    messages["zh-CN"]?.app?.home?.map_nav_cancel ||
    "Cancel"
  );
};

const weChatNavMsg = (lang, key) => {
  const locale = lang === "en" ? EN_US : "zh-CN";
  const home = messages[locale]?.app?.home;
  const fallback = messages["zh-CN"]?.app?.home;
  return home?.[`map_nav_wechat_browser_${key}`] || fallback?.[`map_nav_wechat_browser_${key}`] || key;
};

const isWeChatBrowser = () => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return /MicroMessenger/i.test(ua);
};

const isIOSDevice = () => {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return /iphone|ipod|ipad/i.test(ua);
};

const weChatNavMessage = (lang) => {
  const key = isIOSDevice() ? "message_ios" : "message_android";
  return weChatNavMsg(lang, key);
};

const promptWeChatExternalNavigation = (lang) => {
  showConfirmDialog({
    title: weChatNavMsg(lang, "title"),
    message: weChatNavMessage(lang),
    confirmButtonText: weChatNavMsg(lang, "confirm"),
    showCancelButton: false,
    zIndex: NAV_OVERLAY_Z_INDEX,
  }).catch(() => {});
};

/** 手机端微信内置浏览器：无法唤起地图 App，仅提示用户在系统浏览器打开 */
const blockMobileNavigationInWeChat = (lang) => {
  if (!isWeChatBrowser()) return false;
  promptWeChatExternalNavigation(lang);
  return true;
};

const resolveShouldUseAmap = (useAmap) =>
  typeof useAmap === "boolean" ? useAmap : isInChina();

const iosNavMapMsg = (lang, key) => {
  const locale = lang === "en" ? EN_US : "zh-CN";
  const home = messages[locale]?.app?.home;
  const fallback = messages["zh-CN"]?.app?.home;
  return home?.[`map_nav_ios_map_${key}`] || fallback?.[`map_nav_ios_map_${key}`] || key;
};

const openIOSAppleMapsNavigation = (ctx, lang, { dest, travelMode }) => {
  const dirflg = travelMode === "walking" ? "w" : "d";
  ctx.openWithScheme(`maps://?daddr=${dest}&dirflg=${dirflg}`, {
    failToast: lang === "en" ? "Please install Apple Maps" : "请安装 Apple Maps",
    fallbackUrl: `https://maps.apple.com/?daddr=${dest}&dirflg=${dirflg}`,
    lang,
  });
};

const openIOSGoogleMapsNavigation = (ctx, lang, { dest, travelMode }) => {
  const directionsmode = travelMode === "walking" ? "walking" : "driving";
  // comgooglemaps 的 daddr 仅支持地址或 lat,lng，勿用 %28label%29 格式（Safari 会报网址无效）
  ctx.openWithScheme(
    `comgooglemaps://?daddr=${dest}&directionsmode=${directionsmode}`,
    {
      failToast: lang === "en" ? "Please install Google Maps" : "请安装 Google Maps",
      fallbackUrl: ctx.buildGoogleWebDestUrl({ dest, travelMode }),
      lang,
    }
  );
};

const promptIOSMobileMapNavigation = (
  ctx,
  lang,
  { dest, travelMode, destNameRaw }
) => {
  const appleLabel = iosNavMapMsg(lang, "apple");
  const googleLabel = iosNavMapMsg(lang, "google");
  const pickerTitle = iosNavMapMsg(lang, "picker_title");
  const navPayload = { dest, travelMode, destNameRaw };

  showConfirmDialog({
    title: pickerTitle,
    message: "",
    confirmButtonText: appleLabel,
    cancelButtonText: googleLabel,
    closeOnClickOverlay: false,
    zIndex: NAV_OVERLAY_Z_INDEX,
  })
    .then(() => openIOSAppleMapsNavigation(ctx, lang, navPayload))
    .catch(() => openIOSGoogleMapsNavigation(ctx, lang, navPayload));
};

const openViaAnchorClick = (url) => {
  try {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  } catch {
    return false;
  }
};

const openInSameTab = (url) => {
  window.location.assign(url);
};

const tryOpenNewTab = (url) => {
  const win = window.open(url, "_blank");
  if (win) return { ok: true, win };
  if (openViaAnchorClick(url)) return { ok: true, win: null };
  return { ok: false, win: null };
};

const promptBlockedNavigationFallback = (url, lang) => {
  showConfirmDialog({
    title: blockedNavMsg(lang, "title"),
    message: blockedNavMsg(lang, "message"),
    confirmButtonText: blockedNavMsg(lang, "open"),
    cancelButtonText: blockedNavCancel(lang),
    zIndex: NAV_OVERLAY_Z_INDEX,
  })
    .then(() => {
      if (tryOpenNewTab(url).ok) return;
      showConfirmDialog({
        title: blockedNavMsg(lang, "title"),
        message: blockedNavMsg(lang, "open_current_message"),
        confirmButtonText: blockedNavMsg(lang, "open_current"),
        cancelButtonText: blockedNavCancel(lang),
        zIndex: NAV_OVERLAY_Z_INDEX,
      })
        .then(() => openInSameTab(url))
        .catch(() => {});
    })
    .catch(() => {});
};

const openExternalNavigation = (url, lang = "zh", { afterAsync = false } = {}) => {
  if (!url) return;

  const handleBlocked = () => {
    promptBlockedNavigationFallback(url, lang);
  };

  const opened = tryOpenNewTab(url);
  if (!opened.ok) {
    handleBlocked();
    return;
  }

  if (!afterAsync || !opened.win) return;

  window.setTimeout(() => {
    try {
      if (opened.win.closed) return;
      const href = opened.win.location.href;
      if (!href || href === "about:blank") {
        opened.win.location.replace(url);
        window.setTimeout(() => {
          try {
            if (!opened.win.closed && opened.win.location.href === "about:blank") {
              opened.win.close();
              handleBlocked();
            }
          } catch {
            // 已跳转到外链
          }
        }, 400);
      }
    } catch {
      // 跨域说明新标签页已打开导航
    }
  }, 200);
};

const PI = Math.PI;
const GCJ_A = 6378245.0;
const GCJ_EE = 0.00669342162296594323;

const outOfChina = (lng, lat) =>
  lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;

/** 马来西亚范围：此区域内不做 GCJ 偏移（outOfChina  bbox 过大，会误包含马来） */
const isMalaysiaCoordinate = (lng, lat) => {
  const numLng = Number(lng);
  const numLat = Number(lat);
  return (
    Number.isFinite(numLng) &&
    Number.isFinite(numLat) &&
    numLat >= 0.85 &&
    numLat <= 7.5 &&
    numLng >= 99 &&
    numLng <= 119.5
  );
};

/** 仅中国大陆（含港澳台）坐标需要做 GCJ-02 偏移 */
const shouldApplyGcj02Offset = (lng, lat) => {
  const numLng = Number(lng);
  const numLat = Number(lat);
  if (!Number.isFinite(numLng) || !Number.isFinite(numLat)) return false;
  if (isMalaysiaCoordinate(numLng, numLat)) return false;
  return !outOfChina(numLng, numLat);
};

const transformLat = (lng, lat) => {
  let ret =
    -100.0 +
    2.0 * lng +
    3.0 * lat +
    0.2 * lat * lat +
    0.1 * lng * lat +
    0.2 * Math.sqrt(Math.abs(lng));
  ret +=
    ((20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) *
      2.0) /
    3.0;
  ret +=
    ((20.0 * Math.sin(lat * PI) + 40.0 * Math.sin((lat / 3.0) * PI)) * 2.0) /
    3.0;
  ret +=
    ((160.0 * Math.sin((lat / 12.0) * PI) +
      320 * Math.sin((lat * PI) / 30.0)) *
      2.0) /
    3.0;
  return ret;
};

const transformLng = (lng, lat) => {
  let ret =
    300.0 +
    lng +
    2.0 * lat +
    0.1 * lng * lng +
    0.1 * lng * lat +
    0.1 * Math.sqrt(Math.abs(lng));
  ret +=
    ((20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) *
      2.0) /
    3.0;
  ret +=
    ((20.0 * Math.sin(lng * PI) + 40.0 * Math.sin((lng / 3.0) * PI)) * 2.0) /
    3.0;
  ret +=
    ((150.0 * Math.sin((lng / 12.0) * PI) +
      300.0 * Math.sin((lng / 30.0) * PI)) *
      2.0) /
    3.0;
  return ret;
};

/** 浏览器 WGS84 → 高德 GCJ-02（仅中国大陆坐标） */
const wgs84ToGcj02 = (lng, lat) => {
  if (!shouldApplyGcj02Offset(lng, lat)) return { lng, lat };
  let dLat = transformLat(lng - 105.0, lat - 35.0);
  let dLng = transformLng(lng - 105.0, lat - 35.0);
  const radLat = (lat / 180.0) * PI;
  let magic = Math.sin(radLat);
  magic = 1 - GCJ_EE * magic * magic;
  const sqrtMagic = Math.sqrt(magic);
  dLat = (dLat * 180.0) / (((GCJ_A * (1 - GCJ_EE)) / (magic * sqrtMagic)) * PI);
  dLng = (dLng * 180.0) / ((GCJ_A / sqrtMagic) * Math.cos(radLat) * PI);
  return { lng: lng + dLng, lat: lat + dLat };
};

/** 接口门店坐标（WGS84）→ 高德 GCJ-02 */
const wgs84CoordToAmap = (lat, lng) => {
  const numLat = Number(lat);
  const numLng = Number(lng);
  if (!Number.isFinite(numLat) || !Number.isFinite(numLng)) {
    return { lat: numLat, lng: numLng };
  }
  return wgs84ToGcj02(numLng, numLat);
};

/**
 * 接口门店坐标 → Google / Apple Maps
 * - 中国大陆：Google 在华同样基于 GCJ-02，需做 WGS84→GCJ 偏移
 * - 马来西亚等境外：保持 WGS84，且排除 GCJ 误算
 */
const wgs84CoordToGoogle = (lat, lng) => {
  const numLat = Number(lat);
  const numLng = Number(lng);
  if (!Number.isFinite(numLat) || !Number.isFinite(numLng)) {
    return { lat: numLat, lng: numLng };
  }
  if (shouldApplyGcj02Offset(numLng, numLat)) {
    return wgs84ToGcj02(numLng, numLat);
  }
  return { lat: numLat, lng: numLng };
};

const formatLatLngPair = (lat, lng) => `${Number(lat)},${Number(lng)}`;

const sanitizeGoogleMapLabel = (name) =>
  String(name || "")
    .trim()
    // 半角/全角括号会被 Google Maps 当成标签结束符，需移除（含名称内的 ））
    .replace(/[()（）]/g, " ")
    .replace(/[,，]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Google 导航：精确 lat,lng + 门店名；名称 encode 后包在 %28 %29 内（iOS comgooglemaps daddr） */
const formatGoogleSchemeParam = ({ dest, destNameRaw }) => {
  const name = sanitizeGoogleMapLabel(destNameRaw);
  if (!name) return dest;
  const label = encodeURIComponent(name).replace(/%20/g, "+");
  return `${dest}%28${label}%29`;
};

/**
 * Android google.navigation：官方不支持 lat,lng(label)，q 只能是「店名/地址」或「纯坐标」。
 * 有店名时 q=店名（终点显示名称）、ll=lat,lng（精确导航）；无店名时 q=坐标。
 */
const buildAndroidGoogleNavigationScheme = ({ dest, destNameRaw, travelMode }) => {
  const mode = travelMode === "walking" ? "w" : "d";
  const name = sanitizeGoogleMapLabel(destNameRaw);
  if (name) {
    const q = encodeURIComponent(name).replace(/%20/g, "+");
    return `google.navigation:q=${q}&ll=${dest}&mode=${mode}`;
  }
  return `google.navigation:q=${dest}&mode=${mode}`;
};

const wgs84CachedToGcjOrigin = (cached) => {
  const gcj = wgs84ToGcj02(cached.lng, cached.lat);
  return { lat: gcj.lat, lng: gcj.lng, isGcj: true };
};

const launchAmapNavigation = (navigation, lang, { afterAsync = false } = {}) => {
  if (typeof navigation === "function") {
    navigation();
    return;
  }
  openExternalNavigation(navigation.url, lang, { afterAsync });
};

const buildAmapWebNavigationUrl = (
  ctx,
  { originPoint, dest, destNameRaw, travelMode, lang, callnative = false }
) => {
  const origin = originPoint
    ? formatLatLngPair(originPoint.lat, originPoint.lng)
    : null;
  return ctx.buildAmapWebUrl({
    origin,
    dest,
    destNameRaw,
    travelMode,
    forceOrigin: !!originPoint,
    originNameRaw: lang === "en" ? "My Location" : "当前位置",
    callnative,
  });
};

const buildAmapIosSchemeUrl = ({
  origin,
  dest,
  destNameRaw,
  travelMode = "driving",
  originNameRaw = "当前位置",
}) => {
  const [dLat, dLng] = dest.split(",").map(Number);
  const destName =
    destNameRaw != null && String(destNameRaw).trim() !== ""
      ? String(destNameRaw).trim()
      : "";
  const sourceApplication = "castrol-autofilm";

  if (!origin) {
    const params = [
      `sourceApplication=${encodeURIComponent(sourceApplication)}`,
      destName ? `poiname=${encodeURIComponent(destName)}` : "",
      `lat=${dLat}`,
      `lon=${dLng}`,
      "dev=0",
      `style=${travelMode === "walking" ? "2" : "0"}`,
    ].filter(Boolean);
    return `iosamap://navi?${params.join("&")}`;
  }

  const [sLat, sLng] = origin.split(",").map(Number);
  const t = travelMode === "walking" ? "2" : "0";
  const params = [
    `sourceApplication=${encodeURIComponent(sourceApplication)}`,
    "sid=",
    `slat=${sLat}`,
    `slon=${sLng}`,
    `sname=${encodeURIComponent(originNameRaw || "当前位置")}`,
    "did=",
    `dlat=${dLat}`,
    `dlon=${dLng}`,
    `dname=${encodeURIComponent(destName)}`,
    "dev=0",
    `t=${t}`,
    "m=0",
  ];
  return `iosamap://path?${params.join("&")}`;
};

const openAmapNavigationOnIOS = (
  ctx,
  lang,
  { origin, dest, destNameRaw, travelMode, originNameRaw }
) => {
  const amapWeb = ctx.buildAmapWebUrl({
    origin: origin || undefined,
    dest,
    destNameRaw,
    travelMode,
    forceOrigin: !!origin,
    originNameRaw: originNameRaw || (lang === "en" ? "My Location" : "当前位置"),
    callnative: false,
  });
  const scheme = buildAmapIosSchemeUrl({
    origin,
    dest,
    destNameRaw,
    travelMode,
    originNameRaw:
      originNameRaw || (lang === "en" ? "My Location" : "当前位置"),
  });
  ctx.openWithScheme(scheme, {
    fallbackUrl: amapWeb,
    lang,
  });
};

const buildAmapNavigationPayload = (
  ctx,
  { originPoint, dest, destNameRaw, travelMode, lang, isiOS, isAndroid }
) => {
  const origin = originPoint
    ? formatLatLngPair(originPoint.lat, originPoint.lng)
    : null;
  const amapWeb = buildAmapWebNavigationUrl(ctx, {
    originPoint,
    dest,
    destNameRaw,
    travelMode,
    lang,
    callnative: false,
  });

  // iOS：iosamap://（Android 用 amapuri://）
  if (isiOS) {
    return () => {
      openAmapNavigationOnIOS(ctx, lang, {
        origin,
        dest,
        destNameRaw,
        travelMode,
        originNameRaw: lang === "en" ? "My Location" : "当前位置",
      });
    };
  }

  if (isAndroid) {
    return () => {
      ctx.openWithScheme(
        origin
          ? ctx.buildAmapScheme({ origin, dest, destNameRaw })
          : ctx.buildAmapScheme({ dest, destNameRaw }),
        {
          fallbackUrl: amapWeb,
          lang,
        }
      );
    };
  }

  return { url: amapWeb };
};

const openAmapNavigationWithOrigin = (
  ctx,
  lang,
  { originPoint, dest, destNameRaw, travelMode, isiOS, isAndroid },
  { afterAsync = false } = {}
) => {
  const navigation = buildAmapNavigationPayload(ctx, {
    originPoint,
    dest,
    destNameRaw,
    travelMode,
    lang,
    isiOS,
    isAndroid,
  });
  launchAmapNavigation(navigation, lang, { afterAsync });
};

const openGoogleNavigationWithOrigin = (
  ctx,
  lang,
  { originPoint, dest, travelMode },
  { afterAsync = false } = {}
) => {
  const url = originPoint
    ? ctx.buildGoogleWebDirUrl({
        origin: formatLatLngPair(originPoint.lat, originPoint.lng),
        dest,
        travelMode,
      })
    : ctx.buildGoogleWebDestUrl({ dest, travelMode });
  openExternalNavigation(url, lang, { afterAsync });
};

/**
 * 高德：先获取浏览器定位作为起点再跳转。
 * PC 网页版必须传 from=；定位成功后在 geolocation 回调里同步 window.open，避免 Chrome 拦截。
 */
const openAmapNavigationAfterLocation = (lang, options, buildNavigation) =>
  new Promise((resolve) => {
    const openNav = (originPoint, { inUserGesture = false } = {}) => {
      const navigation = buildNavigation(originPoint);
      launchAmapNavigation(navigation, lang, { afterAsync: !inUserGesture });
    };

    const cached = getCachedBrowserPosition();
    if (cached && options.preferCache !== false) {
      openNav(wgs84CachedToGcjOrigin(cached), { inUserGesture: true });
      resolve();
      return;
    }

    let settled = false;
    let cancelAcquire = null;
    const overallTimeoutMs =
      options.locationOverallTimeout ??
      getNavigationGeoProfile(null).overallTimeout;

    const settle = (originPoint, meta = {}) => {
      if (settled) {
        // 已失败跳转后若定位才成功：补开带起点的导航
        if (originPoint && meta.inUserGesture) {
          closeToast();
          openNav(originPoint, { inUserGesture: true });
        }
        return;
      }
      settled = true;
      if (overallTimer != null) clearTimeout(overallTimer);
      cancelAcquire?.();
      closeToast();

      if (!originPoint) {
        if (meta.reason === "denied" || geolocationAccessDenied) {
          // 用户明确拒绝：静默，仅导航到终点
        } else if (meta.reason === "unavailable") {
          showFailToast(
            lang === "en"
              ? "Mac could not determine your location. Enable Location Services for Chrome in System Settings."
              : "Mac 无法获取位置（kCLErrorLocationUnknown）。请在「系统设置 → 隐私与安全性 → 定位服务」中开启定位，并允许 Chrome"
          );
        } else if (meta.reason === "timeout") {
          showFailToast(
            lang === "en"
              ? "Location timed out. Navigating to destination only."
              : "定位超时，仅导航到终点"
          );
        } else {
          showFailToast(
            lang === "en"
              ? "Location unavailable. Navigating to destination only."
              : "定位失败，仅导航到终点"
          );
        }
      }

      openNav(originPoint, { inUserGesture: meta.inUserGesture === true });
      resolve();
    };

    const overallTimer = window.setTimeout(
      () => settle(null, { reason: "timeout" }),
      overallTimeoutMs
    );

    // 必须在 showLoadingToast 之前同步发起定位，保留 Chrome 用户手势
    cancelAcquire = acquireNavigationOriginInteractive(options, {
      onSuccess: (originPoint) => {
        settle(originPoint, { inUserGesture: true, reason: "ok" });
      },
      onFailure: ({ reason }) => {
        settle(null, { reason });
      },
    });

    showLoadingToast({
      message: lang === "en" ? "Getting location..." : "正在获取定位…",
      duration: 0,
      forbidClick: false,
    });
  });

/**
 * 导航工具类（优化版）
 * - 国内：高德（amap）
 * - 国外：Google（google maps）
 * - macOS 桌面端：国外优先 Apple Maps（maps.apple.com），国内仍走高德网页版/uri
 *
 * 兼容场景：
 * - 移动端：优先使用 URL Scheme 打开 App 导航；失败则 fallback 到网页
 * - 桌面端：使用网页导航链接（新开 tab），避免 scheme 在 Chrome 上失败
 */
const navigatorUtilsOptimized = {
  navigateToMap(navigatorInfo, options = {}) {
    try {
      const {
        startLatitude,
        startLongitude,
        endLatitude,
        endLongitude,
        name,
      } = navigatorInfo || {};
      console.log("navigatorInfo", navigatorInfo);

      const inChina =
        typeof options.useAmap === "boolean" ? options.useAmap : isInChina();
      const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
      const lang = options?.lang === "en" ? "en" : inChina ? "zh" : "en";


      const L = {
        zh: {
          invalidPoints: "请输入完整起终点经纬度",
          invalidFormat: "经纬度格式不正确",
          navigationFailed: "导航失败",
          installAmap: "请安装高德导航",
          installGoogleMaps: "请安装 Google Maps",
          installAppleMaps: "请安装 Apple Maps",
        },
        en: {
          invalidPoints: "Please enter complete start/end coordinates",
          invalidFormat: "Invalid coordinate format",
          navigationFailed: "Navigation failed",
          installAmap: "Please install Amap",
          installGoogleMaps: "Please install Google Maps",
          installAppleMaps: "Please install Apple Maps",
        },
      };

      const msg = L[lang];

      if (
        startLatitude == null ||
        startLongitude == null ||
        endLatitude == null ||
        endLongitude == null
      ) {
        showFailToast(msg.invalidPoints);
        return;
      }

      const isAndroid = /android/i.test(ua);
      const isiOS = /iphone|ipod|ipad/i.test(ua);
      const isMac =
        /macintosh|mac os x/i.test(ua) && !/win/i.test(ua) && /safari/i.test(ua);

      const travelMode = options?.travelMode ?? "driving"; // driving | walking
      const travelParam = travelMode === "walking" ? "walking" : "driving";

      // 格式化
      let sLat = Number(startLatitude);
      let sLng = Number(startLongitude);
      let dLat = Number(endLatitude);
      let dLng = Number(endLongitude);

      if (
        [sLat, sLng, dLat, dLng].some((v) => !Number.isFinite(v)) ||
        Number.isNaN(sLat) ||
        Number.isNaN(sLng) ||
        Number.isNaN(dLat) ||
        Number.isNaN(dLng)
      ) {
        showFailToast(msg.invalidFormat);
        return;
      }

      // 坐标系：国内高德用 GCJ-02，Google/Apple 在中国境内也用 GCJ-02，境外用 WGS84
      if (inChina) {
        if (!options.startIsGcj) {
          const gcjStart = wgs84ToGcj02(sLng, sLat);
          sLat = gcjStart.lat;
          sLng = gcjStart.lng;
        }
        if (!options.destinationIsGcj) {
          const gcjEnd = wgs84CoordToAmap(dLat, dLng);
          dLat = gcjEnd.lat;
          dLng = gcjEnd.lng;
        }
      } else if (!options.destinationIsGcj) {
        const googleEnd = wgs84CoordToGoogle(dLat, dLng);
        dLat = googleEnd.lat;
        dLng = googleEnd.lng;
      }

      const origin = `${sLat},${sLng}`;
      const dest = `${dLat},${dLng}`;
      /** 终点名称保持明文，仅在拼 URL 时编码一次，避免网页版 to 被二次 encode 显示 %E5%xx */
      const destNameRaw =
        name != null && String(name).trim() !== "" ? String(name).trim() : "";

      const amapWebOptions = {
        dest,
        destNameRaw,
        travelMode,
        origin: origin || undefined,
        forceOrigin: !!origin,
        originNameRaw: lang === "en" ? "My Location" : "当前位置",
      };

      // 统一入口：优先移动端 scheme，桌面端直接网页
      if (isAndroid) {
        if (inChina) {
          // 高德 App
          const amapScheme = this.buildAmapScheme({
            origin,
            dest,
            destNameRaw,
          });
          this.openWithScheme(amapScheme, {
            failToast: msg.installAmap,
            fallbackUrl: this.buildAmapWebUrl(amapWebOptions),
            lang,
          });
        } else {
          // Google App
          const googleScheme = `google.navigation:q=${dest}&saddr=${origin}&mode=${travelParam}`;
          this.openWithScheme(googleScheme, {
            failToast: msg.installGoogleMaps,
            fallbackUrl: this.buildGoogleWebDirUrl({
              origin,
              dest,
              travelMode: travelMode,
            }),
            lang,
          });
        }
        return;
      }

      if (isiOS) {
        if (inChina) {
          openAmapNavigationOnIOS(this, lang, {
            origin,
            dest,
            destNameRaw,
            travelMode,
            originNameRaw: lang === "en" ? "My Location" : "当前位置",
          });
        } else {
          // iOS：Apple Maps scheme（更快），fallback 到 maps.apple.com
          const appleScheme = `maps://?saddr=${origin}&daddr=${dest}&dirflg=${travelMode === "walking" ? "w" : "d"}`;
          this.openWithScheme(appleScheme, {
            failToast: msg.installAppleMaps,
            fallbackUrl: this.buildAppleMapsWebUrl({ origin, dest, travelMode }),
            lang,
          });
        }
        return;
      }

      // 桌面端：mac 优先 Apple Maps（国外），国内走高德网页
      if (isMac && !inChina) {
        const appleWeb = this.buildAppleMapsWebUrl({ origin, dest, travelMode });
        window.open(appleWeb, "_blank");
        return;
      }

      if (inChina) {
        const amapWeb = this.buildAmapWebUrl(amapWebOptions);
        window.open(amapWeb, "_blank");
      } else {
        const googleWeb = this.buildGoogleWebDirUrl({
          origin,
          dest,
          travelMode: travelMode,
        });
        window.open(googleWeb, "_blank");
      }
    } catch (e) {
      showFailToast(L[lang].navigationFailed);
    }
  },

  /**
   * 通过 URL Scheme 唤起原生 App。
   * 移动端：仅尝试一次 scheme；若页面未切到后台，再同页跳转网页（callnative=0，不再二次唤起 App）。
   */
  openWithScheme(
    schemeUrl,
    { failToast, fallbackUrl, waitMs = 2500, lang = "zh" } = {}
  ) {
    if (!schemeUrl) return;

    const isiOS = isIOSDevice();
    const isMobile = isLikelyMobileDevice();
    const effectiveWaitMs = isMobile
      ? Math.max(1200, Math.min(waitMs, 1800))
      : waitMs;
    let fallbackCancelled = false;
    let timerId = null;

    const removeListeners = () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", cancelFallback);
      window.removeEventListener("blur", onWindowBlur);
    };

    const cancelFallback = () => {
      fallbackCancelled = true;
      if (timerId != null) {
        clearTimeout(timerId);
        timerId = null;
      }
      removeListeners();
    };

    const onVisibilityChange = () => {
      if (document.hidden) cancelFallback();
    };

    const onWindowBlur = () => {
      if (isMobile) cancelFallback();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", cancelFallback);
    if (isMobile) {
      window.addEventListener("blur", onWindowBlur, { once: true });
    }

    const launchSchemeOnce = () => {
      if (isMobile) {
        try {
          const iframe = document.createElement("iframe");
          iframe.style.cssText =
            "display:none;width:0;height:0;border:0;visibility:hidden";
          iframe.src = schemeUrl;
          document.body.appendChild(iframe);
          window.setTimeout(() => iframe.remove(), 2000);
        } catch {
          // ignore
        }

        // iOS 高德：iframe 偶发唤不起，补一次 scheme（仍等 visibility 判定，不与网页 fallback 同时跳）
        if (isiOS && /^iosamap:\/\//i.test(schemeUrl)) {
          window.setTimeout(() => {
            if (fallbackCancelled || document.hidden) return;
            try {
              window.location.href = schemeUrl;
            } catch {
              // ignore
            }
          }, 400);
        }
        return;
      }

      try {
        window.location.href = schemeUrl;
      } catch {
        // ignore
      }
    };

    launchSchemeOnce();

    if (!fallbackUrl) {
      window.setTimeout(removeListeners, effectiveWaitMs + 500);
      return;
    }

    timerId = window.setTimeout(() => {
      removeListeners();
      if (fallbackCancelled) return;

      const webUrl = String(fallbackUrl).replace(
        /([?&])callnative=1(?=&|$)/,
        "$1callnative=0"
      );
      try {
        window.location.replace(webUrl);
      } catch {
        window.location.href = webUrl;
      }
    }, effectiveWaitMs);
  },

  // 高德：Android scheme（iOS 见 buildAmapIosSchemeUrl）
  buildAmapScheme({ origin, dest, destNameRaw }) {
    const [dLat, dLng] = dest.split(",");
    const dname =
      destNameRaw && String(destNameRaw).trim() !== ""
        ? encodeURIComponent(String(destNameRaw).trim())
        : "";
    if (!origin) {
      return `amapuri://route/plan/?sourceApplication=castrol-autofilm&dlat=${dLat}&dlon=${dLng}&dname=${dname}&dev=0`;
    }
    const [sLat, sLng] = origin.split(",");
    return `amapuri://route/plan/?sourceApplication=castrol-autofilm&slat=${sLat}&slon=${sLng}&dlat=${dLat}&dlon=${dLng}&dname=${dname}&dev=0`;
  },

  buildAmapIosScheme(params) {
    return buildAmapIosSchemeUrl(params);
  },

  // 高德：网页导航（origin 可选，不传则仅规划到终点）
  buildAmapWebUrl({
    origin,
    dest,
    destNameRaw,
    originNameRaw = "当前位置",
    travelMode = "driving",
    forceOrigin = false,
    callnative = false,
  }) {
    const [dLat, dLng] = dest.split(",").map(Number);
    const safeDestName = destNameRaw
      ? String(destNameRaw).trim().replace(/,/g, " ")
      : "";
    let toPart = `${dLng},${dLat}`;
    if (safeDestName) {
      toPart += `,${encodeURIComponent(safeDestName)}`;
    }

    const modeMap = { driving: "car", walking: "walk", transit: "bus" };
    const mode = modeMap[travelMode] || "car";
    const params = [];

    if (origin) {
      const [sLat, sLng] = origin.split(",").map(Number);
      if (forceOrigin) {
        const safeOriginName = String(originNameRaw || "当前位置").replace(
          /,/g,
          " "
        );
        params.push(
          `from=${sLng},${sLat},${encodeURIComponent(safeOriginName)}`
        );
      } else {
        const sameCoord =
          Number(sLat).toFixed(6) === Number(dLat).toFixed(6) &&
          Number(sLng).toFixed(6) === Number(dLng).toFixed(6);
        const tooClose =
          !sameCoord && haversineKm(sLat, sLng, dLat, dLng) < 0.08;
        if (!sameCoord && !tooClose) {
          const safeOriginName = String(originNameRaw || "当前位置").replace(
            /,/g,
            " "
          );
          params.push(
            `from=${sLng},${sLat},${encodeURIComponent(safeOriginName)}`
          );
        }
      }
    }

    params.push(`to=${toPart}`);
    params.push(`mode=${mode}`);
    params.push("policy=0");
    params.push("coordinate=gaode");
    params.push(`callnative=${callnative ? 1 : 0}`);
    params.push("src=castrol-autofilm");
    return `https://uri.amap.com/navigation?${params.join("&")}`;
  },

  buildGoogleWebDestUrl({ dest, travelMode = "driving" }) {
    const mode = travelMode === "walking" ? "walking" : "driving";
    // 网页版用精确 lat,lng；勿 encode 逗号
    return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=${mode}`;
  },

  /**
   * PC 端打开导航
   * - 国内：高德网页版（浏览器定位作为起点，via from= 参数）
   * - 国外：Google Maps 网页版（有定位则传 origin，否则仅传终点由用户输入起点）
   */
  async navigateToDestinationPc(navigatorInfo, options = {}) {
    const lang = options?.lang === "en" ? "en" : "zh";
    const L = {
      zh: {
        invalidPoints: "目的地经纬度无效",
        invalidFormat: "经纬度格式不正确",
        navigationFailed: "导航失败",
      },
      en: {
        invalidPoints: "Invalid destination coordinates",
        invalidFormat: "Invalid coordinate format",
        navigationFailed: "Navigation failed",
      },
    };
    const msg = L[lang];

    try {
      const { endLatitude, endLongitude, name, region, useAmap } =
        navigatorInfo || {};
      let dLat = Number(endLatitude);
      let dLng = Number(endLongitude);

      if (!Number.isFinite(dLat) || !Number.isFinite(dLng)) {
        showFailToast(msg.invalidPoints);
        return;
      }
      if (dLat === 0 && dLng === 0) {
        showFailToast(msg.invalidPoints);
        return;
      }

      const destNameRaw =
        name != null && String(name).trim() !== "" ? String(name).trim() : "";
      const travelMode = options?.travelMode ?? "driving";
      const googleDestOptions = { dest: "", destNameRaw, travelMode };

      const shouldUseAmap = resolveShouldUseAmap(useAmap);

      if (shouldUseAmap) {
        const gcjDest = wgs84CoordToAmap(dLat, dLng);
        dLat = gcjDest.lat;
        dLng = gcjDest.lng;
      } else {
        const googleDest = wgs84CoordToGoogle(dLat, dLng);
        dLat = googleDest.lat;
        dLng = googleDest.lng;
      }

      const dest = `${dLat},${dLng}`;
      googleDestOptions.dest = dest;

      if (shouldUseAmap) {
        if (options.skipInternalGeolocation) {
          let originPoint = null;
          if (
            options.originWgs84?.lat != null &&
            options.originWgs84?.lng != null
          ) {
            const gcj = wgs84ToGcj02(
              options.originWgs84.lng,
              options.originWgs84.lat
            );
            originPoint = { lat: gcj.lat, lng: gcj.lng };
          }
          openAmapNavigationWithOrigin(
            this,
            lang,
            {
              originPoint,
              dest,
              destNameRaw,
              travelMode,
              isiOS: false,
              isAndroid: false,
            },
            { afterAsync: options.navigationAfterAsync === true }
          );
          return;
        }

        await openAmapNavigationAfterLocation(lang, options, (originPoint) => {
          const origin = originPoint
            ? formatLatLngPair(originPoint.lat, originPoint.lng)
            : null;
          return {
            url: this.buildAmapWebUrl({
              origin,
              dest,
              destNameRaw,
              travelMode,
              forceOrigin: !!originPoint,
              originNameRaw: lang === "en" ? "My Location" : "当前位置",
            }),
          };
        });
        return;
      }

      if (options.skipInternalGeolocation) {
        let originPoint = null;
        if (
          options.originWgs84?.lat != null &&
          options.originWgs84?.lng != null
        ) {
          originPoint = wgs84CoordToGoogle(
            options.originWgs84.lat,
            options.originWgs84.lng
          );
        }
        openGoogleNavigationWithOrigin(
          this,
          lang,
          { originPoint, ...googleDestOptions },
          { afterAsync: options.navigationAfterAsync === true }
        );
        return;
      }

      openExternalNavigation(this.buildGoogleWebDestUrl(googleDestOptions), lang);
    } catch (e) {
      showFailToast(msg.navigationFailed);
    }
  },

  /**
   * 手机端打开导航（国内/国外由 isInChina 决定）
   * - 国内：高德（iOS / Android）
   * - 国外 iOS：Apple Maps / Google Maps 可选
   * - 国外 Android：Google Maps
   */
  async navigateToDestinationMobile(navigatorInfo, options = {}) {
    const lang = options?.lang === "en" ? "en" : "zh";
    if (blockMobileNavigationInWeChat(lang)) return;

    const L = {
      zh: {
        invalidPoints: "目的地经纬度无效",
        invalidFormat: "经纬度格式不正确",
        navigationFailed: "导航失败",
      },
      en: {
        invalidPoints: "Invalid destination coordinates",
        invalidFormat: "Invalid coordinate format",
        navigationFailed: "Navigation failed",
      },
    };
    const msg = L[lang];

    try {
      const { endLatitude, endLongitude, name, region, useAmap } =
        navigatorInfo || {};
      let dLat = Number(endLatitude);
      let dLng = Number(endLongitude);

      if (!Number.isFinite(dLat) || !Number.isFinite(dLng)) {
        showFailToast(msg.invalidPoints);
        return;
      }
      if (dLat === 0 && dLng === 0) {
        showFailToast(msg.invalidPoints);
        return;
      }

      const shouldUseAmap = resolveShouldUseAmap(useAmap);

      if (shouldUseAmap) {
        const gcjDest = wgs84CoordToAmap(dLat, dLng);
        dLat = gcjDest.lat;
        dLng = gcjDest.lng;
      } else {
        const googleDest = wgs84CoordToGoogle(dLat, dLng);
        dLat = googleDest.lat;
        dLng = googleDest.lng;
      }

      const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
      const isAndroid = /android/i.test(ua);
      const isiOS = /iphone|ipod|ipad/i.test(ua);
      const travelMode = options?.travelMode ?? "driving";
      const dest = `${dLat},${dLng}`;
      const destNameRaw =
        name != null && String(name).trim() !== "" ? String(name).trim() : "";
      const googleDestOptions = { dest, destNameRaw, travelMode };

      if (shouldUseAmap) {
        // iOS：点击后立即 iosamap://navi，不等浏览器定位（async 后会丢失用户手势）
        if (isiOS) {
          let origin = null;
          if (
            options.skipInternalGeolocation &&
            options.originWgs84?.lat != null &&
            options.originWgs84?.lng != null
          ) {
            const gcj = wgs84ToGcj02(
              options.originWgs84.lng,
              options.originWgs84.lat
            );
            origin = formatLatLngPair(gcj.lat, gcj.lng);
          }
          openAmapNavigationOnIOS(this, lang, {
            origin,
            dest,
            destNameRaw,
            travelMode,
            originNameRaw: lang === "en" ? "My Location" : "当前位置",
          });
          return;
        }

        if (options.skipInternalGeolocation) {
          let originPoint = null;
          if (
            options.originWgs84?.lat != null &&
            options.originWgs84?.lng != null
          ) {
            const gcj = wgs84ToGcj02(
              options.originWgs84.lng,
              options.originWgs84.lat
            );
            originPoint = { lat: gcj.lat, lng: gcj.lng };
          }
          openAmapNavigationWithOrigin(
            this,
            lang,
            {
              originPoint,
              dest,
              destNameRaw,
              travelMode,
              isiOS,
              isAndroid,
            },
            { afterAsync: options.navigationAfterAsync === true }
          );
          return;
        }

        await openAmapNavigationAfterLocation(lang, options, (originPoint) =>
          buildAmapNavigationPayload(this, {
            originPoint,
            dest,
            destNameRaw,
            travelMode,
            lang,
            isiOS,
            isAndroid,
          })
        );
        return;
      }

      // iOS：可选 Apple Maps 或 Google Maps，不传起点由 App 自行定位
      if (isiOS) {
        promptIOSMobileMapNavigation(this, lang, googleDestOptions);
        return;
      }

      if (isAndroid) {
        const googleScheme = buildAndroidGoogleNavigationScheme({
          ...googleDestOptions,
          travelMode,
        });
        this.openWithScheme(googleScheme, {
          failToast:
            lang === "en" ? "Please install Google Maps" : "请安装 Google Maps",
          fallbackUrl: this.buildGoogleWebDestUrl(googleDestOptions),
          lang,
        });
        return;
      }

      const googleWeb = this.buildGoogleWebDestUrl(googleDestOptions);
      openExternalNavigation(googleWeb, lang);
    } catch (e) {
      showFailToast(msg.navigationFailed);
    }
  },

  // Google：网页导航
  buildGoogleWebDirUrl({ origin, dest, travelMode }) {
    const mode = travelMode === "walking" ? "walking" : "driving";
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=${mode}`;
  },

  // Apple Maps：网页导航（mac/桌面更稳）
  buildAppleMapsWebUrl({ origin, dest, travelMode }) {
    const dirflg = travelMode === "walking" ? "w" : "d";
    return `https://maps.apple.com/?saddr=${origin}&daddr=${dest}&dirflg=${dirflg}`;
  },

  /**
   * PC 地图页预加载定位（静默），点击导航时可直接用缓存，避免 Chrome 异步弹窗拦截。
   * 需 HTTPS；用户未授权时浏览器会弹权限框。
   */
  warmNavigationGeolocationCache() {
    if (getCachedBrowserPosition()) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    if (isLikelyMobileDevice()) return;

    queryGeolocationPermissionState().then((state) => {
      if (state !== "granted") return;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const parsed = readGeolocationCoords(position);
          if (parsed) saveBrowserPosition(parsed.lat, parsed.lng);
        },
        () => {},
        {
          enableHighAccuracy: false,
          timeout: 15000,
          maximumAge: 300000,
        }
      );
    });
  },
};

export default navigatorUtilsOptimized;

